import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Send, MessageSquare, ShieldOff, ScrollText, BadgeCheck, CheckCircle2 } from "lucide-react";

const TOPICS = [
  { value: "viewer", label: "I'm a viewer with a question" },
  { value: "creator", label: "I want to submit a movie" },
  { value: "partnership", label: "Partnership / press / business" },
  { value: "report", label: "Report inappropriate content" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("viewer");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Please write at least 10 characters so we can help.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        topic,
        subject: subject.trim(),
        message: message.trim(),
      });
      setDone(true);
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Couldn't send right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[760px] mx-auto px-6 py-24 text-center" data-testid="contact-success">
        <div className="h-14 w-14 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-green-400" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-6">
          Message received.
        </h1>
        <p className="text-white/60 mt-3 max-w-md mx-auto">
          We read every note. Expect a reply at <span className="text-white font-medium break-all">{email}</span> within 48 hours — usually sooner.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <Link to="/" className="ghost-btn">Back to home</Link>
          <Link to="/browse" className="ember-btn" data-testid="contact-go-browse">Browse the library</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-20" data-testid="contact-page">
      <p className="text-eyebrow text-[#E65F2B]">Get in touch</p>
      <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-3 leading-[0.95]" data-testid="contact-headline">
        We read every <span className="text-[#E65F2B]">message</span>.
      </h1>
      <p className="text-white/60 text-lg max-w-2xl mt-5 leading-relaxed">
        Whether you've got a question about your subscription, a film you'd like to submit, or a partnership you want to explore — drop us a line. We reply within 48 hours.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8 mt-12">
        {/* Form */}
        <form onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-eyebrow mb-2 block">Your name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                className="input-dark"
                placeholder="Adaeze Okonkwo"
                disabled={busy}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
                className="input-dark"
                placeholder="you@example.com"
                disabled={busy}
                data-testid="contact-email-input"
              />
            </div>
          </div>

          <div>
            <label className="text-eyebrow mb-2 block">What can we help with?</label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="input-dark"
              disabled={busy}
              data-testid="contact-topic-select"
            >
              {TOPICS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-eyebrow mb-2 block">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="input-dark"
              placeholder="One short line summarizing your message"
              disabled={busy}
              data-testid="contact-subject-input"
            />
          </div>

          <div>
            <label className="text-eyebrow mb-2 block">Message</label>
            <textarea
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              className="input-dark resize-none"
              placeholder="Tell us what's on your mind. Be as specific as you can."
              disabled={busy}
              data-testid="contact-message-input"
            />
            <p className="text-xs text-white/30 mt-1">{message.length}/5000</p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-white/40">
              We'll never share your email. Replies come from admins, not bots.
            </p>
            <button
              type="submit"
              disabled={busy || message.trim().length < 10}
              className="ember-btn disabled:opacity-40"
              data-testid="contact-submit"
            >
              <Send className="h-4 w-4" /> {busy ? "Sending..." : "Send message"}
            </button>
          </div>
        </form>

        {/* Side rail */}
        <aside className="space-y-3">
          <div className="card-surface p-5">
            <Mail className="h-5 w-5 text-[#E65F2B] mb-2" />
            <p className="font-display font-bold">Direct email</p>
            <a href="mailto:hello@christoreel.com" className="text-sm text-white/70 hover:text-white block mt-1 break-all">
              hello@christoreel.com
            </a>
            <p className="text-xs text-white/40 mt-2">
              Prefer email? Write us directly — the same admins read this inbox.
            </p>
          </div>

          <Link to="/submission-guidelines" className="card-surface p-5 block hover:border-[#E65F2B]/40 transition-colors" data-testid="contact-link-submission">
            <ScrollText className="h-5 w-5 text-[#EAB308] mb-2" />
            <p className="font-display font-bold">Submission guidelines</p>
            <p className="text-xs text-white/40 mt-1">
              Before pitching a film, read the specs and content rules.
            </p>
          </Link>

          <Link to="/about" className="card-surface p-5 block hover:border-[#E65F2B]/40 transition-colors" data-testid="contact-link-about">
            <BadgeCheck className="h-5 w-5 text-green-400 mb-2" />
            <p className="font-display font-bold">About Christoreel</p>
            <p className="text-xs text-white/40 mt-1">
              Why we built this, and who we serve.
            </p>
          </Link>

          <div className="card-surface p-5">
            <ShieldOff className="h-5 w-5 text-red-400 mb-2" />
            <p className="font-display font-bold">Reporting abuse</p>
            <p className="text-xs text-white/40 mt-1">
              See content that violates our guidelines? Pick "Report inappropriate content" in the form. We act fast.
            </p>
          </div>

          <div className="card-surface p-5">
            <MessageSquare className="h-5 w-5 text-[#E65F2B] mb-2" />
            <p className="font-display font-bold">Response time</p>
            <p className="text-xs text-white/40 mt-1">
              We reply to every message — typically within 48 hours, often sooner. No tickets, no bots.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
